/**
 * Instance-config reader for the extraction scripts (NOT the pure core). The
 * cross-vendor egress opt-in lives in `.agent/vortex.json` → `ontos.verify
 * .crossVendorEgress`; the scripts read it DIRECTLY so a user who sets it once is
 * honored every run — without depending on the agent remembering to pass a flag.
 *
 * FAIL CLOSED everywhere: a missing/unreadable/malformed config, or the key being
 * anything other than the boolean `true`, yields `false` (egress stays off). This is
 * a privacy gate — the only value that turns it on is an explicit `true`.
 */

import { readFileSync } from "node:fs";
import { join } from "node:path";

/** Pure: is cross-vendor egress opted in, given an already-parsed config value? */
export function crossVendorEgressFromConfigObject(cfg: unknown): boolean {
  if (typeof cfg !== "object" || cfg === null) return false;
  const ontos = (cfg as Record<string, unknown>).ontos;
  if (typeof ontos !== "object" || ontos === null) return false;
  const verify = (ontos as Record<string, unknown>).verify;
  if (typeof verify !== "object" || verify === null) return false;
  return (verify as Record<string, unknown>).crossVendorEgress === true; // strictly true
}

/**
 * Read the opt-in from `<dataDir>/../.agent/vortex.json`. `dataDir` is the instance
 * data dir (`.../data`); the config is its sibling. Any I/O or parse error ⇒ false.
 */
export function crossVendorEgressFromConfig(dataDir: string): boolean {
  try {
    const cfgPath = join(dataDir, "..", ".agent", "vortex.json");
    return crossVendorEgressFromConfigObject(JSON.parse(readFileSync(cfgPath, "utf8")));
  } catch {
    return false; // fail closed
  }
}

/**
 * Pure: is CONVERSATION-transcript extraction opted in? (2026-06-29) Mining every closed
 * transcript (one LLM call per ~6000-char chunk) is the heavy, token-explosive path; the graph's
 * core (decisions, repeated failures, rules) comes for free from the deterministic RECORDS
 * extractor (`rebuild-ontos`). So conversation mining is OPT-IN: only the literal boolean `true`
 * turns it on. Default OFF protects every instance — especially ones that just `npx vortex update`
 * — from a surprise token bill.
 */
export function conversationExtractionFromConfigObject(cfg: unknown): boolean {
  if (typeof cfg !== "object" || cfg === null) return false;
  const ontos = (cfg as Record<string, unknown>).ontos;
  if (typeof ontos !== "object" || ontos === null) return false;
  return (ontos as Record<string, unknown>).conversationExtraction === true; // opt-in: strictly true
}

/** Read the conversation-extraction opt-in from `<dataDir>/../.agent/vortex.json`. Any I/O or parse error ⇒ false (off). */
export function conversationExtractionFromConfig(dataDir: string): boolean {
  try {
    const cfgPath = join(dataDir, "..", ".agent", "vortex.json");
    return conversationExtractionFromConfigObject(JSON.parse(readFileSync(cfgPath, "utf8")));
  } catch {
    return false; // fail closed = extraction OFF (cheap default; opt-in)
  }
}
